'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useSession, signOut } from 'next-auth/react'
import { useState, useEffect, useRef } from 'react'

export default function Header() {
  const pathname = usePathname()
  const { data: session, status } = useSession()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => pathname === path
  const isLoading = status === 'loading'

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  // Close menus on navigation
  useEffect(() => {
    setUserMenuOpen(false)
    setMobileMenuOpen(false)
  }, [pathname])

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 backdrop-blur-sm bg-white/95">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2 group">
            <span className="text-3xl group-hover:scale-110 transition-transform duration-200">🎣</span>
            <span className="text-xl font-bold text-ocean-700">Fish Weigh</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              href="/"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                isActive('/') 
                  ? 'bg-ocean-100 text-ocean-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Home
            </Link>
            <Link
              href="/events"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                pathname.startsWith('/events')
                  ? 'bg-ocean-100 text-ocean-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Events
            </Link>
            <Link
              href="/clubs"
              className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                pathname.startsWith('/clubs')
                  ? 'bg-ocean-100 text-ocean-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Clubs
            </Link>
            {session && (
              <Link
                href="/dashboard"
                className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                  isActive('/dashboard') 
                    ? 'bg-ocean-100 text-ocean-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Dashboard
              </Link>
            )}
          </div>

          {/* Auth Buttons / User Menu - Desktop */}
          <div className="hidden md:flex items-center space-x-3">
            {isLoading ? (
              <div className="px-4 py-2 text-gray-400">Loading...</div>
            ) : session ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {session.user.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">{session.user.role}</p>
                  </div>
                  <div className="w-8 h-8 bg-ocean-600 text-white rounded-full flex items-center justify-center font-semibold">
                    {(session.user.name?.[0] || session.user.email?.[0] || 'U').toUpperCase()}
                  </div>
                  <svg 
                    className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{session.user.name || 'User'}</p>
                      <p className="text-xs text-gray-500 truncate">{session.user.email}</p>
                    </div>
                    
                    <Link
                      href="/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">📊</span>
                        Dashboard
                      </span>
                    </Link>
                    
                    <Link
                      href="/events"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">📅</span>
                        Events
                      </span>
                    </Link>
                    
                    <Link
                      href="/clubs"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">🏛️</span>
                        Clubs
                      </span>
                    </Link>
                    
                    <Link
                      href="/members"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">👥</span>
                        Members Area
                      </span>
                    </Link>
                    
                    <Link
                      href="/dashboard/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">⚙️</span>
                        Settings
                      </span>
                    </Link>
                    
                    {session.user.role === 'SUPER_ADMIN' && (
                      <Link
                        href="/admin"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <span className="flex items-center">
                          <span className="mr-2">👑</span>
                          Admin Dashboard
                        </span>
                      </Link>
                    )}
                    
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false)
                          signOut({ callbackUrl: '/' })
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <span className="flex items-center">
                          <span className="mr-2">🚪</span>
                          Sign Out
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  href="/auth/signin"
                  className="px-4 py-2 text-ocean-600 hover:bg-ocean-50 rounded-lg transition-colors duration-200"
                >
                  Sign In
                </Link>
                <Link
                  href="/auth/signup"
                  className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 animate-slide-down">
            <div className="flex flex-col space-y-2">
              <Link
                href="/"
                className={`px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActive('/') 
                    ? 'bg-ocean-100 text-ocean-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                href="/events"
                className={`px-4 py-3 rounded-lg transition-colors duration-200 ${
                  pathname.startsWith('/events')
                    ? 'bg-ocean-100 text-ocean-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="flex items-center">
                  <span className="mr-2">📅</span>
                  Events
                </span>
              </Link>
              <Link
                href="/clubs"
                className={`px-4 py-3 rounded-lg transition-colors duration-200 ${
                  pathname.startsWith('/clubs')
                    ? 'bg-ocean-100 text-ocean-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="flex items-center">
                  <span className="mr-2">🏛️</span>
                  Clubs
                </span>
              </Link>
              {session && (
                <Link
                  href="/dashboard"
                  className={`px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive('/dashboard') 
                      ? 'bg-ocean-100 text-ocean-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}
              
              <div className="border-t border-gray-200 pt-2 mt-2">
                {session ? (
                  <>
                    {/* User info in mobile */}
                    <div className="px-4 py-3 bg-ocean-50 rounded-lg mb-2">
                      <p className="text-sm font-medium text-gray-900">{session.user.name || 'User'}</p>
                      <p className="text-xs text-gray-500">{session.user.email}</p>
                      <p className="text-xs text-ocean-600 font-semibold mt-1">{session.user.role}</p>
                    </div>
                    
                    <Link
                      href="/clubs"
                      className="block px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">🏛️</span>
                        Clubs
                      </span>
                    </Link>
                    
                    <Link
                      href="/members"
                      className="block px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">👥</span>
                        Members Area
                      </span>
                    </Link>
                    
                    <Link
                      href="/dashboard/settings"
                      className="block px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center">
                        <span className="mr-2">⚙️</span>
                        Settings
                      </span>
                    </Link>
                    
                    {session.user.role === 'SUPER_ADMIN' && (
                      <Link
                        href="/admin"
                        className="block px-4 py-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <span className="flex items-center">
                          <span className="mr-2">👑</span>
                          Admin Dashboard
                        </span>
                      </Link>
                    )}
                    
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false)
                        signOut({ callbackUrl: '/' })
                      }}
                      className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 mt-2"
                    >
                      <span className="flex items-center">
                        <span className="mr-2">🚪</span>
                        Sign Out
                      </span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/auth/signin"
                      className="block px-4 py-3 text-ocean-600 hover:bg-ocean-50 rounded-lg transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/auth/signup"
                      className="block px-4 py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors duration-200 mt-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

